import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { AgGridModule } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { CommonColDef, IconColDef } from '../../../../shared/datacenter/column-defs';
import { ClickIconCellComponent } from '../../../../shared/datacenter/cell-renderers/click-icon-cell.component';
import { User } from '../../../../shared/models/user';
import { UserService } from '../../../../shared/services/user.service';
import { UserNotifications } from '../../../models/v2/user-notifications';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserCardService } from '../../../services/user-card.service';
import { PersonMember } from '../../../../shared/models/user-info';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

import { AddSubscriptionDialogComponent } from './dialog-add-subscription/dialog-add-subscription.component';
import { DialogRemoveNotificationComponent } from './dialog-remove-notification/dialog-remove-notification.component';

const firstTabIndex = 0;

@Component({
	selector: 'copo-user-self-card-notification',
	templateUrl: './user-self-card-notification.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './user-self-card-notification.component.css',
	imports: [
		NgIf,

		MatButtonModule,
		TranslateModule,
		MatIconModule,

		MatFormFieldModule,
		AgGridModule,
		MatSelectModule,
		NgFor,

		MatOptionModule,

		LoadingSpinnerComponent
	]
})
export class UserCardNotificationComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	userNotifications: UserNotifications[] = [];
	personMembers?: PersonMember[];
	selectedPersonmember?: PersonMember;
	userId!: string;
	logedInUser?: User;
	fetched = false;
	canManageOrganizationUsers = false;
	enableUsertoUpdate = false;
	routeSub: Subscription = Subscription.EMPTY;
	addsubscriptionDialogSub: Subscription = Subscription.EMPTY;
	readonly modules = clientSideAgGridModules;

	gridOptions!: CommonGridOptions<UserNotifications>;
	organizationsList: { organizationId: number; organizationName: string }[] = [];
	selectedOrganizationId: number | 'ALL' | null = 'ALL';
	groupedNotifications: {
		organizationId: number;
		organizationName: string;
		supplierName: string;
		notifications: UserNotifications[];
	}[] = [];

	filteredGroups: {
		organizationId: number;
		organizationName: string;
		supplierName: string;
		notifications: UserNotifications[];
	}[] = [];

	supplierIndex = firstTabIndex;

	private notificationSub: Subscription = Subscription.EMPTY;
	private deleteNotificationSub: Subscription = Subscription.EMPTY;
	private dialogSubscription: Subscription = new Subscription();

	constructor(
		private readonly userCardService: UserCardService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
	) {
		this.gridOptions = new CommonGridOptions<UserNotifications>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new CommonColDef<UserNotifications, string>({
					headerName: this.translationService.getTR('user.card.notifications.tab.service'),
					valueGetter: params => {
						const { data } = params;
						if (!data) {
							return undefined;
						}
						return data.subscribers.service.name;
					},
					flex: 1,
					comparator: (valueA, valueB) =>
						!valueA ? -1 : !valueB ? 1 : valueA.toLocaleLowerCase().localeCompare(valueB.toLowerCase())
				}),
				new CommonColDef<UserNotifications>({
					headerName: this.translationService.getTR('user.card.notifications.tab.subscription'),
					valueGetter: params => {
						const { data } = params;
						if (!data) {
							return undefined;
						}
						return data.subscribers.name;
					},
					flex: 1
				}),
				new CommonColDef<UserNotifications>({
					headerName: this.translationService.getTR('user.card.notifications.tab.description'),
					valueGetter: params => {
						const { data } = params;
						if (!data) {
							return undefined;
						}
						return data.subscribers.description;
					},
					flex: 2
				}),
				new CommonColDef<UserNotifications>({
					headerName: this.translationService.getTR('user.card.notifications.tab.frequency'),
					valueGetter: params => {
						switch (params.data?.frequency) {
						case 0: return this.translationService.getTR('notifications.immediatly');
						case 1: return this.translationService.getTR('notifications.daily');
						case 7: return this.translationService.getTR('notifications.weekly');
						case 30: return this.translationService.getTR('notifications.monthly');
						default: return '';
						}
					},
					maxWidth: 120
				}),
				new IconColDef<UserNotifications>({
					headerName: this.translationService.getTR('users.details-card.actions'),
					suppressMenu: true,
					minWidth: 60,
					maxWidth: 60,
					width: 60,
					cellRenderer: ClickIconCellComponent,
					cellRendererParams: {
						iconName: 'delete_bin',
						onIconClick: (params: ICellRendererParams<UserNotifications, unknown>) => {
							const notif = params.data;
							if (notif) {
								this.onDeleteIcon(notif);
							}
						}
					}
				})
			]
		});
	}

	ngOnInit() {
		this.fetched = false;
		if (!this.route.parent?.parent) {
			throw new Error('wrong url');
		}
		this.routeSub = this.route.parent.paramMap.subscribe(params => {
			const idString = params.get('id');
			if (idString === null) {
				return;
			}
			this.userId = idString;
		});
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}

		this.userId = this.router.url.includes('self') ? loginUser.id.toString() : this.userId;

		this.setNotifications(loginUser).catch((err: unknown) => {
			console.error(err);
		});
	}

	getRouteParam() {
		return this.userId;
	}

	ngOnDestroy() {
		this.notificationSub.unsubscribe();
		this.dialogSubscription.unsubscribe();
		this.deleteNotificationSub.unsubscribe();
		this.addsubscriptionDialogSub.unsubscribe();
	}

	changeButton(index: number) {
		this.supplierIndex = index;
	}

	openAddSubscriptionDialog() {
		if (this.enableUsertoUpdate) {
			const addSubscriptionDialog = this.dialog.open<AddSubscriptionDialogComponent>(
				AddSubscriptionDialogComponent,
				{
					width: '70%',
					data: {
						userId: this.userId,
						orgId: this.selectedPersonmember?.member?.id
					}
				}
			);
			this.addsubscriptionDialogSub = addSubscriptionDialog.afterClosed().subscribe(async () => {
				const userNotifications = await firstValueFrom(this.userCardService.getUserNotifications(this.userId));
				this.userNotifications = userNotifications;
				this.constructNotifications();
			});
		}
	}

	onDeleteIcon(notification: UserNotifications) {
		const dialogRef = this.dialog.open(DialogRemoveNotificationComponent, {
			data: notification
		});

		this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.deleteNotification(notification.id, notification.organizations.organizationId);
			}
		});
	}

	handleOrganizationNotificationsUpdated(orgId: number | 'ALL') {
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return;
		}

		this.selectedOrganizationId = orgId;

		if (orgId !== 'ALL') {
			this.selectedPersonmember = this.personMembers?.find(pm => pm.member?.id === orgId);
			if (this.selectedPersonmember) {
				// Check authorization for THIS specific organization
				this.canManageOrganizationUsers = this.selectedPersonmember.member
					? loginUser.canUpdateOrganizationUsers(this.selectedPersonmember.member.id)
					: false;
				this.enableUsertoUpdate = this.isSelfCard() || this.canManageOrganizationUsers;
			}
		} else {
			// ALL mode - disable Add button (can't add to "ALL", needs specific org context)
			this.canManageOrganizationUsers = false;
			this.enableUsertoUpdate = false;
		}

		this.applyFilter();
	}

	private applyFilter() {
		this.filteredGroups = this.selectedOrganizationId === 'ALL'
			? this.groupedNotifications
			: this.groupedNotifications.filter(g => g.organizationId === this.selectedOrganizationId);
	}

	private isSelfCard() {
		const loginUser = this.userService.loggedUser;
		if (loginUser === undefined) {
			return false;
		}
		return this.router.url.includes('self') || this.userId === loginUser.id.toString();
	}

	private buildGroups() {
		const groupMap = new Map<number, {
			organizationId: number;
			organizationName: string;
			supplierName: string;
			notifications: UserNotifications[];
		}>();

		for (const n of this.userNotifications) {
			const orgId = n.organizations.organizationId;
			const existing = groupMap.get(orgId);
			if (existing) {
				existing.notifications.push(n);
			} else {
				groupMap.set(orgId, {
					organizationId: orgId,
					organizationName: n.organizations.organizationName,
					supplierName: n.organizations.suppliers.supplierName,
					notifications: [n]
				});
			}
		}

		this.groupedNotifications = Array.from(groupMap.values());
		this.organizationsList = this.groupedNotifications.map(g => ({
			organizationId: g.organizationId,
			organizationName: g.organizationName
		}));
	}

	private async setNotifications(loginUser: User) {
		const userInfo = await firstValueFrom(this.userService.getUserDetailsById(
			this.userId,
			['personMembers.member', 'language']
		));
		this.personMembers = userInfo.personMembers;

		if (this.personMembers.length === 0) {
			this.fetched = true;
			return;
		}

		this.selectedPersonmember =	userInfo.personMembers.find(
			personMember => personMember.member?.id === loginUser.currentVisibility.organizationId
		);

		if (!this.selectedPersonmember) {
			this.selectedPersonmember = userInfo.personMembers[0];
		}
		this.canManageOrganizationUsers = this.selectedPersonmember.member
			? loginUser.canUpdateOrganizationUsers(this.selectedPersonmember.member.id) : false;
		this.enableUsertoUpdate = this.isSelfCard() || this.canManageOrganizationUsers;

		this.notificationSub = this.userCardService
			.getUserNotifications(this.userId)
			.subscribe({
				next: userNotifications => {
					this.userNotifications = userNotifications;
					this.buildGroups();
					if (this.organizationsList.length > 0) {
						const firstOrgId = this.organizationsList[0].organizationId;
						this.handleOrganizationNotificationsUpdated(firstOrgId);
					} else {
						this.selectedOrganizationId = 'ALL';
						this.applyFilter();
					}
					this.fetched = true;
				},
				error: (err: unknown) => {
					this.fetched = true;
					console.error(err);
				}
			});
	}

	private constructNotifications() {
		this.buildGroups();
		this.applyFilter();
	}

	private deleteNotification(notificationId: number, organizationId: number) {
		if (!this.userId) {
			return;
		}
		this.deleteNotificationSub = this.userCardService
			.deleteUserNotification(this.userId, organizationId, notificationId)
			.subscribe({
				next: () => {
					this.userNotifications = this.userNotifications.filter(n => n.id !== notificationId);
					this.constructNotifications();
					this.fetched = true;
				},
				error: (err: unknown) => {
					this.fetched = true;
					console.error(err);
				}
			});
	}
}
